import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useMemo, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { components } from 'react-select';
import { styled } from '@mui/material/styles';
import MuiBox from '@mui/material/Box';
import MuiTypography from '@mui/material/Typography';
import { useMultiSelect } from '../context/MultiSelectProvider.js';

const EllipsisWrapper = styled(MuiBox) `
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: inherit;
  display: flex;
  flex: 1;
`;
const MockEllipsis = styled(MuiBox) `
  position: absolute;
  top: 0;
  left: 0;
  z-index: 999;
  visibility: hidden;
`;
const HiddenItems = styled(MuiTypography, { shouldForwardProp: prop => !['isDisabled'].includes(prop) }) `
  width: fit-content;
  color: ${({ theme, isDisabled }) => theme.palette[isDisabled ? 'N30' : 'N05'].main};
`;
const ValueContainer = (_a) => {
    var { getValue, children, options, selectProps } = _a, rest = __rest(_a, ["getValue", "children", "options", "selectProps"]);
    const ref = useRef(null);
    const mockRef = useRef(null);
    const [itemsVisible, setItemsVisible] = useState(0);
    const [overflown, setOverflown] = useState(false);
    const [mockText, setMockText] = useState();
    const { search, allItemsSelectedLabel } = useMultiSelect();
    const currentValue = getValue();
    const isAllSelected = useMemo(() => options.length === currentValue.length, [options, currentValue]);
    const value = useMemo(() => {
        if (isEmpty(currentValue)) {
            return children;
        }
        let valueToRender = null;
        if (isAllSelected && !(search === null || search === void 0 ? void 0 : search.value)) {
            valueToRender = (jsx(MuiTypography, Object.assign({ variant: "body2" }, { children: allItemsSelectedLabel }), "value"));
        }
        else {
            valueToRender = (jsx(MuiTypography, Object.assign({ noWrap: true, variant: "body1" }, { children: currentValue.reduce((memo, { label }) => (memo ? `${memo}, ${label}` : label), '') }), "value"));
        }
        const dummyInput = children.slice(-1).pop();
        return [valueToRender, dummyInput];
    }, [currentValue, isAllSelected, search, allItemsSelectedLabel, children]);
    useEffect(() => {
        if (mockRef.current && ref.current && currentValue) {
            const labelText = currentValue.map(item => item.label).join(', ');
            setMockText({ label: labelText, count: currentValue.length });
        }
    }, [currentValue]);
    useEffect(() => {
        if (!mockRef.current || !ref.current) {
            return;
        }
        const isOverflown = mockRef.current.clientWidth > ref.current.clientWidth;
        if (!isOverflown && (mockText === null || mockText === void 0 ? void 0 : mockText.count)) {
            setItemsVisible(mockText.count);
        }
        setOverflown(isOverflown);
    }, [mockText]);
    const hiddenItems = useMemo(() => {
        if (itemsVisible >= currentValue.length) {
            return 0;
        }
        return currentValue.length - itemsVisible;
    }, [itemsVisible, currentValue]);
    const renderHiddenItems = () => {
        if (overflown && !isAllSelected && hiddenItems > 0) {
            const displayHiddenItems = hiddenItems - 1;
            return (jsx(HiddenItems, Object.assign({ variant: "body2", isDisabled: selectProps.isDisabled }, { children: displayHiddenItems > 0 && `+${displayHiddenItems}` })));
        }
        return null;
    };
    return (jsxs(components.ValueContainer, Object.assign({}, rest, { selectProps: selectProps, options: options, getValue: getValue }, { children: [jsx(MockEllipsis, Object.assign({ ref: mockRef }, { children: mockText === null || mockText === void 0 ? void 0 : mockText.label })), jsx(EllipsisWrapper, Object.assign({ ref: ref, className: "react-select__multi-value__label" }, { children: value })), renderHiddenItems()] })));
};

export { ValueContainer as default };
//# sourceMappingURL=ValueContainer.js.map
